import kvStatManager, { KV_REPORT_KEY_FACE_FOCUS } from '../kv-stat-manager';
import { isNumber } from '../../utils/utils';
import { VirtualBackgroundMode } from './vb';
import Logger from '../../utils/log/logger';
import type { VisionTaskRegistry } from '../vision';

type Position = {
  sx: number;
  sy: number;
  cropWidth: number;
  cropHeight: number;
  scaleRatio?: number;
  scaleOffsetX?: number;
  scaleOffsetY?: number;
  timestamp: number;
};

export class CenterFace {
  private animationState: {
    current: Position | null;
    target: Position | null;
    animating: boolean;
    debounceTimer: number | null;
    startTime: number;
    duration: number;
    debounceTime: number;
    movementThreshold: number;
    debounceThreshold: number;
  };
  private originalAspect: number;
  private totalOffsetX = 0;
  private totalOffsetY = 0;
  private defaultScaleRatio = 0.10;
  private isRecovering = false;
  private boundaryY = 280;
  private lastActionTime = 0;
  private restTime = 400;
  constructor(private vbMode: number, videoElement: HTMLVideoElement, private faceDetectorHash: string, private visionTaskRegistry: VisionTaskRegistry, private logger:Logger, originalAspect: number = 4 / 3) {
    this.animationState = {
      current: null,
      target: null,
      animating: false,
      debounceTimer: null,
      startTime: 0,
      duration: 3000, // 动画持续时间(毫秒)
      debounceTime: 150, // 防抖时间(毫秒)
      movementThreshold: 30, // 运动阈值(比例)
      debounceThreshold: 15 // 防抖阈值(比例)
    };
    this.addEvent(this.vbMode, !!this.faceDetectorHash);
    this.originalAspect = originalAspect ? originalAspect : 4 / 3;
    this.visionTaskRegistry.setVideo(this.faceDetectorHash, videoElement);
  }
  // 根据方案不同进行数据上报
  private addEvent(type:VirtualBackgroundMode, isSuccess: boolean, error?: number) {
    const eventConfig = [
      { key: KV_REPORT_KEY_FACE_FOCUS.DualStageCropping, error: error ?? (isSuccess ? undefined : 11) },
      { key: KV_REPORT_KEY_FACE_FOCUS.MovingPortrait, error: error ?? (isSuccess ? undefined : 22) }
    ];
    const config = eventConfig[type - 1];
    if (!config) return;
    isSuccess ? kvStatManager.addSuccessEvent({ key: config.key }) : kvStatManager.addFailedEvent({ key: config.key, error: config.error });
  }
  public actionCentering(videoElement: HTMLVideoElement) {
    const now = Date.now(); // 用于降低人脸检测频次至1s/次
    this.animation(); // 开始人脸居中动画
    if (!this.faceDetectorHash || now - this.lastActionTime < this.restTime || this.animationState.animating) return;
    const { videoWidth, videoHeight } = videoElement;
    // 防抖距离和移动距离阙值，防抖距离用来判断人脸是否还在移动，移动距离用来判断跟是否需要再次进行居中
    this.animationState.debounceThreshold = videoWidth / 30;
    this.animationState.movementThreshold = videoWidth / 20;
    this.boundaryY = videoHeight * 0.6;
    try {
      const detections = this.visionTaskRegistry.getResult(this.faceDetectorHash);

      if (detections.detections.length <= 0) {
        if (this.vbMode === VirtualBackgroundMode.Blur) this.recoverOriginal(videoWidth, videoHeight); // 使用裁剪方案情况下，检测不到人脸时恢复原画面
        return;
      }
      // 获取人脸矩形框参数
      const faceBox = detections.detections[0].boundingBox;
      const faceBoxOriginX = faceBox.originX;
      const faceBoxOriginY = faceBox.originY;
      const faceBoxWidth = faceBox.width;
      const faceBoxHeight = faceBox.height;
      // 选择方案进行人脸居中
      if (this.vbMode === VirtualBackgroundMode.Blur) {
        this.dualStageCropping(
          videoWidth,
          videoHeight,
          faceBoxOriginX,
          faceBoxOriginY,
          faceBoxWidth,
          faceBoxHeight
        );
      } else if (this.vbMode === VirtualBackgroundMode.Image) {
        this.movingPortrait(
          videoWidth,
          videoHeight,
          faceBoxOriginX,
          faceBoxOriginY,
          faceBoxWidth,
          faceBoxHeight
        );
      }
    } catch (error) {
      this.logger.error(error);
    }
    this.lastActionTime = now;
  }
  // 计算人脸距离上下和左右边界的最小距离以及需要移动的距离
  private calculateBoundary(
    faceCenter: number,
    videoSize: number,
    origin: number,
    size: number
  ): { min: number, offset: number } {
    let min: number;
    let offset: number;
    if (faceCenter > videoSize / 2) {
      min = videoSize - origin - size;
      offset = origin - min;
    } else {
      min = origin;
      offset = 0;
    }
    return { min, offset };
  }

  private calculateTargetPosition(
    originX: number,
    originY: number,
    width: number,
    height: number,
    videoWidth: number,
    videoHeight: number,
    ratio = 0.4
  ): Position {
    const faceCenterX = originX + width / 2;
    const faceCenterY = originY + height / 2;
    let cropWidth: number;
    let cropHeight: number;
    let { min: minHor, offset: sx } = this.calculateBoundary(faceCenterX, videoWidth, originX, width);
    let { min: minVer, offset: sy } = this.calculateBoundary(faceCenterY, videoHeight, originY, height);
    // 计算画面裁剪后的宽高
    cropWidth = 2 * minHor + width;
    cropHeight = 2 * minVer + height;
    // 根据原视频比例调整宽高至同比例，以便直接放大
    if (cropWidth / cropHeight > this.originalAspect) {
      cropWidth = cropHeight * this.originalAspect;
      sx = faceCenterX - cropWidth / 2;
    } else {
      cropHeight = cropWidth / this.originalAspect;
      sy = faceCenterY - cropHeight / 2;
    }
    // 边界情况处理
    if (width / videoWidth > ratio) {
      sx = 0;
      sy = 0;
      cropWidth = videoWidth;
      cropHeight = videoHeight;
    }
    sx = Math.max(0, Math.min(sx, videoWidth - cropWidth));
    sy = Math.max(0, Math.min(sy, videoHeight - cropHeight));
    return {
      sx,
      sy,
      cropWidth,
      cropHeight,
      timestamp: Date.now()
    };
  }
  private processFacePositionCrop(
    newPosition: Position,
    videoWidth: number,
    videoHeight: number
  ): void {
    if (!this.animationState.current || !this.animationState.target) {
      const originPosition = {
        sx: 0,
        sy: 0,
        cropWidth: videoWidth,
        cropHeight: videoHeight,
        timestamp: Date.now()
      };
      this.animationState.current = originPosition;
      this.animationState.target = originPosition;
      return;
    }
    const movementBounce = this.positionDistance(
      this.animationState.target,
      newPosition
    );
    const movement = this.positionDistance(
      this.animationState.current,
      newPosition
    );
    const cropRatio = this.animationState.current.cropWidth / videoWidth;
    if (movementBounce > this.animationState.debounceThreshold * cropRatio) {
      clearTimeout(this.animationState.debounceTimer as number);
      this.animationState.animating = false;
    }
    if (
      !this.animationState.animating &&
      movement > this.animationState.movementThreshold * cropRatio
    ) {
      this.animationState.target = newPosition;
      this.animationState.debounceTimer = setTimeout(() => {
        this.animationState.startTime = Date.now();
        this.animationState.animating = true;
      }, this.animationState.debounceTime);
    }
  }
  private processFacePositionPortrait(newPosition: Position): void {
    if (!this.animationState.current || !this.animationState.target) {
      this.animationState.current = { ...newPosition };
      this.animationState.target = { ...newPosition };
      return;
    }
    const movementBounce = this.positionDistance(
      this.animationState.current,
      newPosition
    );
    const movement = this.positionDistance(
      this.animationState.target,
      newPosition
    );
    if (movementBounce > this.animationState.debounceThreshold) {
      clearTimeout(this.animationState.debounceTimer as number);
      this.animationState.animating = false;
    }
    if (
      !this.animationState.animating &&
      movement > this.animationState.movementThreshold
    ) {
      this.animationState.current = newPosition;
      this.animationState.debounceTimer = setTimeout(() => {
        this.animationState.startTime = Date.now();
        this.animationState.animating = true;
      }, this.animationState.debounceTime);
    }
  }
  private animation(): void {
    if (!this.animationState.animating) return;
    const elapsed = Date.now() - this.animationState.startTime;
    const progress = Math.min(elapsed / this.animationState.duration, 1);
    const easing = (t: number): number => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    if (this.animationState.current && this.animationState.target) {
      const offsetX =
        (this.animationState.target.sx - this.animationState.current.sx) *
        easing(progress);
      this.animationState.current.sx += offsetX;
      this.totalOffsetX += offsetX;
      const offsetY =
        (this.animationState.target.sy - this.animationState.current.sy) *
        easing(progress);
      this.animationState.current.sy += offsetY;
      this.totalOffsetY += offsetY;
      this.animationState.current.cropWidth
        += (this.animationState.target.cropWidth -
          this.animationState.current.cropWidth) *
        easing(progress);
      this.animationState.current.cropHeight
        += (this.animationState.target.cropHeight -
          this.animationState.current.cropHeight) *
        easing(progress);
      if (this.animationState.current.scaleRatio && this.animationState.target.scaleRatio) {
        this.animationState.current.scaleRatio += (this.animationState.target.scaleRatio - this.animationState.current.scaleRatio) * easing(progress);
      }
      if (isNumber(this.animationState.current.scaleOffsetX) && isNumber(this.animationState.target.scaleOffsetX) && isNumber(this.animationState.current.scaleOffsetY)  && isNumber(this.animationState.target.scaleOffsetY)) {
        const offsetX = (this.animationState.target.scaleOffsetX -
          this.animationState.current.scaleOffsetX) * easing(progress);
        this.animationState.current.scaleOffsetX
          += offsetX;
        const offsetY = (this.animationState.target.scaleOffsetY -
          this.animationState.current.scaleOffsetY) * easing(progress);
        this.animationState.current.scaleOffsetY
          += offsetY;
      }
    }
    if (progress >= 1) {
      this.animationState.animating = false;
      this.animationState.current = this.animationState.target;
      this.isRecovering = false;
    }
  }
  private positionDistance(pos1: Position, pos2: Position): number {
    const posDiff = Math.sqrt((pos1.sx - pos2.sx) ** 2 + (pos1.sy - pos2.sy) ** 2);
    return posDiff;
  }
  private recoverOriginal(videoWidth: number, videoHeight: number): void {
    this.animationState.target = {
      sx: 0,
      sy: 0,
      cropWidth: videoWidth,
      cropHeight: videoHeight,
      timestamp: Date.now()
    };
    this.animationState.animating = true;
    this.animationState.startTime = Date.now();
    this.isRecovering = true;
  }
  private dualStageCropping(
    videoWidth: number,
    videoHeight: number,
    faceBoxOriginX: number,
    faceBoxOriginY: number,
    faceBoxWidth: number,
    faceBoxHeight: number,
    ratio = 0.3
  ): void {
    if (this.isRecovering) return;
    const newTarget = this.calculateTargetPosition(
      faceBoxOriginX,
      faceBoxOriginY,
      faceBoxWidth,
      faceBoxHeight,
      videoWidth,
      videoHeight
    );
    this.processFacePositionCrop(newTarget, videoWidth, videoHeight);
    // 人脸占裁剪后画面过大时，恢复原画面
    if (faceBoxWidth * faceBoxHeight / newTarget.cropWidth / newTarget.cropHeight > ratio) {
      this.recoverOriginal(videoWidth, videoHeight);
    }
  }
  private movingPortrait(
    videoWidth: number,
    videoHeight: number,
    faceBoxOriginX: number,
    faceBoxOriginY: number,
    faceBoxWidth: number,
    faceBoxHeight: number
  ) {
    const newTarget = {
      sx: faceBoxOriginX + faceBoxWidth / 2 + this.totalOffsetX, // 初次偏移计算，进行人像居中
      sy: faceBoxOriginY + faceBoxHeight / 2 + this.totalOffsetY,
      cropWidth: videoWidth,
      cropHeight: videoHeight,
      scaleRatio: this.animationState.current?.scaleRatio ?? 1, // 根据人像大小判断放大还是缩小
      scaleOffsetX: this.animationState.current?.scaleOffsetX ?? 0, // 根据缩放后人脸位置二次矫正位置，判断偏移量，确保始终在中心
      scaleOffsetY: this.animationState.current?.scaleOffsetY ?? 0,
      timestamp: Date.now()
    };
    this.animationState.target = {
      sx: videoWidth / 2,
      sy: faceBoxOriginY + faceBoxHeight / 2,
      cropWidth: videoWidth,
      cropHeight: videoHeight,
      scaleRatio: this.animationState.target?.scaleRatio ?? 1,
      scaleOffsetX: this.animationState.target?.scaleOffsetX ?? 0,
      scaleOffsetY: this.animationState.target?.scaleOffsetY ?? 0,
      timestamp: Date.now()
    };
    if (!this.animationState.animating) {
      this.animationState.target.scaleRatio = Math.sqrt(faceBoxWidth * faceBoxHeight / videoWidth / videoHeight / this.defaultScaleRatio);
      this.animationState.target.scaleOffsetX = -this.animationState.target.scaleRatio / 2 + 0.5;
      this.animationState.target.scaleOffsetY = -this.animationState.target.scaleRatio + 1;
      if ((this.animationState.target.sy - this.animationState.target.scaleOffsetY * this.animationState.target.cropHeight) / this.animationState.target.scaleRatio < this.boundaryY) {
        this.animationState.target.scaleOffsetY = (this.animationState.target.sy - this.boundaryY * this.animationState.target.scaleRatio) / this.animationState.target.cropHeight;
      }
    }
    this.processFacePositionPortrait(newTarget);
  }
  set aspectRatio(aspect: number) {
    this.originalAspect = aspect;
  }
  get current(): Position | null {
    return this.animationState.current;
  }
  get offset() {
    return {
      offsetX: this.totalOffsetX,
      offsetY: this.totalOffsetY
    };
  }
  get targetCrop(): Position | null {
    return this.animationState.target;
  }
}
